import React from 'react';
import { Navigate, Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage/plugin-tech-radar';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
// `@backstage/plugin-auth` is the frontend half of Backstage's CIMD OAuth flow for MCP
// clients (see app-config.yaml's top-level auth.clientIdMetadataDocuments, enabled for
// /api/mcp-actions/v1/{catalog,agents}). Its only export is a New Frontend System
// `FrontendPlugin` (package.json's backstage.features -> "@backstage/FrontendPlugin";
// verified in node_modules/@backstage/plugin-auth/dist/plugin.esm.js: it mounts a page at
// "/oauth2" with route "/oauth2/authorize/:sessionId" -> ConsentPage). It's installed below
// as a native `features` entry alongside the rest of this app, which is otherwise carried
// over unchanged from the legacy frontend system via @backstage/core-compat-api's
// convertLegacyAppOptions/convertLegacyAppRoot compatibility layer (see
// https://backstage.io/docs/frontend-system/building-apps/migrating). This is what makes the
// backend's CIMD approval redirect to /oauth2/authorize/:sessionId actually render instead
// of 404ing.
import authPlugin from '@backstage/plugin-auth';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
  convertLegacyRouteRef,
  convertLegacyRouteRefs,
} from '@backstage/core-compat-api';
import { createApp } from '@backstage/frontend-defaults';

import { apis, Auth0AuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
  SignInProviderConfig,
} from '@backstage/core-components';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { attachComponentData } from '@backstage/core-plugin-api';
import { createPermission } from '@backstage/plugin-permission-common';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  AgentAdminPage,
  AgentChatPage,
  AgentDetailsPage,
  AgentUsagePage,
  CreateAgentPage,
} from '@rpt/backstage-plugin-agent-platform';
import { ModuleRegistryPage } from '@rpt/plugin-module-registry';
import {
  ModulePickerFieldExtension,
  VersionPickerFieldExtension,
  ModuleVariablesFieldExtension,
} from '@rpt/plugin-scaffolder-module-fields';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';

/**
 * The admin surface is permission-gated from its first route. Also referenced by
 * packages/backend/src/agent-platform's admin route handlers and by the custom permission
 * policy in packages/backend/src/permissions, which must define this permission identically
 * (name + attributes) since there is no shared `-common` package between the frontend app and
 * this backend module to import it from — matching this repo's existing pattern of
 * independently defining `catalogEntityCreatePermission`-style permissions on each side that
 * needs to check them.
 */
export const agentPlatformAdminPermission = createPermission({
  name: 'agent-platform.admin',
  attributes: { action: 'update' },
});

const oauthProvider: SignInProviderConfig = {
  id: 'auth0',
  title: 'Sign in with Auth0',
  message: 'Use your danquack.dev account',
  apiRef: Auth0AuthApiRef,
};

// `CatalogImportPage` is a legacy routable extension (`createRoutableExtension` from
// @backstage/plugin-catalog-import), which internally calls `useRouteRef` on its own mount
// point and throws if that mount point wasn't discovered in the app element tree. Backstage's
// legacy `core-app-api` route collector (routingV2Collector, active here since this app isn't
// on the react-router-6-beta) walks recursively through wrapper elements like
// `RequirePermission` to find that mount point — but `@backstage/core-compat-api`'s
// `collectLegacyRoutes` (verified by reading
// node_modules/@backstage/core-compat-api/dist/collectLegacyRoutes.esm.js) only checks the
// route's `element` prop itself, one level deep. Wrapping `CatalogImportPage` directly in
// `RequirePermission` as the route element would therefore hide its `core.plugin`/
// `core.mountPoint` component data from the New Frontend System conversion, breaking both
// CatalogImportPage's own render (it would throw) and every external route binding that
// targets `catalogImportPlugin.routes.importPage` (scaffolder's and api-docs's
// "register a component/API" links below). Re-attaching the same component data to this
// small wrapper (exactly what `createRoutableExtension` does internally) keeps the permission
// gate and restores that discovery.
const CatalogImportRoute = () => (
  <RequirePermission permission={catalogEntityCreatePermission}>
    <CatalogImportPage />
  </RequirePermission>
);
attachComponentData(
  CatalogImportRoute,
  'core.mountPoint',
  catalogImportPlugin.routes.importPage,
);
attachComponentData(CatalogImportRoute, 'core.plugin', catalogImportPlugin);

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog" />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route
      path="/create"
      element={
        <ScaffolderPage>
          <ScaffolderFieldExtensions>
            <ModulePickerFieldExtension />
            <VersionPickerFieldExtension />
            <ModuleVariablesFieldExtension />
          </ScaffolderFieldExtensions>
        </ScaffolderPage>
      }
    />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route path="/catalog-import" element={<CatalogImportRoute />} />
    <Route path="/module-registry" element={<ModuleRegistryPage />} />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/agent-platform/chat" element={<AgentChatPage />} />
    <Route path="/agent-platform/usage" element={<AgentUsagePage />} />
    <Route
      path="/agent-platform/admin"
      element={
        <RequirePermission permission={agentPlatformAdminPermission}>
          <AgentAdminPage />
        </RequirePermission>
      }
    />
    <Route
      path="/agent-platform/admin/create"
      element={
        <RequirePermission permission={agentPlatformAdminPermission}>
          <CreateAgentPage />
        </RequirePermission>
      }
    />
    <Route
      path="/agent-platform/admin/:namespace/:name"
      element={
        <RequirePermission permission={agentPlatformAdminPermission}>
          <AgentDetailsPage />
        </RequirePermission>
      }
    />
    <Route
      path="/agent-platform/admin/:namespace/:name/edit"
      element={
        <RequirePermission permission={agentPlatformAdminPermission}>
          <CreateAgentPage />
        </RequirePermission>
      }
    />
  </FlatRoutes>
);

// Phase 1 of https://backstage.io/docs/frontend-system/building-apps/migrating: wrap the
// existing legacy `createApp` options and root element tree with `@backstage/core-compat-api`
// so every existing plugin, API, route and the custom sign-in flow keep behaving exactly as
// they did under `@backstage/app-defaults`'s `createApp`/`<FlatRoutes>`, while the app itself
// runs on the New Frontend System (`@backstage/frontend-defaults`) so that
// `@backstage/plugin-auth`'s native FrontendPlugin (CIMD's consent UI) can be installed
// alongside it.
const convertedAppOptions = convertLegacyAppOptions({
  apis,
  components: {
    SignInPage: props => (
      <SignInPage {...props} auto providers={[oauthProvider]} />
    ),
  },
});

const convertedAppRoot = convertLegacyAppRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);

const app = createApp({
  features: [convertedAppOptions, ...convertedAppRoot, authPlugin],
  bindRoutes({ bind }) {
    // `bindRoutes` still binds the same legacy plugin route-ref objects as before. Each
    // legacy RouteRef/ExternalRouteRef is mutated in place and cached the first time it's
    // converted (verified in
    // node_modules/@backstage/core-compat-api/dist/convertLegacyRouteRef.esm.js:
    // `convertLegacyRouteRef` checks `"$$type" in ref` and returns the *same object* once
    // converted), and `collectLegacyRoutes` (invoked by `convertLegacyAppRoot` above) converts
    // these exact same plugin singletons' `.routes`/`.externalRoutes` when it builds each
    // plugin's `createFrontendPlugin`. Converting them again here via `convertLegacyRouteRef(s)`
    // is required to satisfy the New Frontend System's `bind()` typing but resolves to the
    // identical object each plugin's generated page extension already registered, so every
    // binding below targets the exact same route it did before the migration.
    bind(convertLegacyRouteRefs(catalogPlugin.externalRoutes), {
      createComponent: convertLegacyRouteRef(scaffolderPlugin.routes.root),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
      createFromTemplate: convertLegacyRouteRef(
        scaffolderPlugin.routes.selectedTemplate,
      ),
    });
    bind(convertLegacyRouteRefs(apiDocsPlugin.externalRoutes), {
      registerApi: convertLegacyRouteRef(catalogImportPlugin.routes.importPage),
    });
    bind(convertLegacyRouteRefs(scaffolderPlugin.externalRoutes), {
      registerComponent: convertLegacyRouteRef(
        catalogImportPlugin.routes.importPage,
      ),
      viewTechDoc: convertLegacyRouteRef(techdocsPlugin.routes.docRoot),
    });
    bind(convertLegacyRouteRefs(orgPlugin.externalRoutes), {
      catalogIndex: convertLegacyRouteRef(catalogPlugin.routes.catalogIndex),
    });
  },
});

export default app.createRoot();
